import React from 'react';
import PropTypes from 'prop-types';
import RequestHandler from '../libs/requestHandling';
import '../../polyfills/polyfill-allSettled';
import { withRouter } from '../libs/widgetUtilities';
import CacheResultsContext from '../components/CacheResultsContext';
import LoadingState from '../styled components/LoadingState';
import FormatSettingsContext from '../components/FormatSettingsContext';
import { dateFormatOptions } from '../libs/dateFormatting';
import NavigationHistory from '../libs/NavigationHistory';
import { serializeYesplanAPIDate, todayDateObject } from '../libs/dateTimeUtilities';
import TimesheetDetail from '../timesheets/TimesheetDetail/TimesheetDetail';
import { cacheResultForTimesheetsKey } from '../libs/cacheKeys';
import { formatTimesheet } from '../libs/ypAPIDataFormatter';
import ContainerWrapper from '../styled components/ContainerWrapper';
import TimesheetError from '../components/Errors/TimesheetError';
import DateNavigationUrlWrapper from '../components/DateNavigationUrlWrapper';
import YPTimesheetObject from '../yesplan_classes/YPTimesheetObject';
import NoPermissionOnResourceError from '../components/Errors/NoPermissionOnResourceError';
import NotInTeamUsingTimesheetsError from '../components/Errors/NotInTeamUsingTimesheetsError';
import { capture, PH_TIMESHEET_SUBMITTED } from '../libs/posthogAnalytics';

class TimesheetContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      allContentFetched: false,
      errorCode: false,
      timesheet: null
    };
    this.dateObject = props.getDateFromUrlOrFromCurrentDate('timesheet');
    if (!this.dateObject || this.dateObject.isAfter(todayDateObject())) {
      props.navigationHistory.replaceUrl(props.linkUrlFor('/timesheets/'));
    } else {
      props.replaceUrlWithDateUrl('timesheet');
      this.autosaveTimesheet = this.autosaveTimesheet.bind(this);
      this.submitTimesheet = this.submitTimesheet.bind(this);
      this.fetchTimesheetAndOnlyUpdateHistory =
        this.fetchTimesheetAndOnlyUpdateHistory.bind(this);
    }
  }

  componentDidMount() {
    if (this.dateObject) {
      this.fetchTimesheetAndCounters(this.dateObject);
    }
  }

  componentWillUnmount() {
    const { timesheet } = this.state;
    if (timesheet !== null && timesheet.hasChanged()) {
      this.autosaveTimesheet(timesheet);
      this.props.resetCacheResultValue(cacheResultForTimesheetsKey);
    }
  }

  getTimesheet(startDateObject) {
    const { user } = this.props;

    return new RequestHandler().get(
      '/timesheet',
      {
        yesplanurl: user.host,
        resourceid: user.resource.id,
        userid: user.id,
        on: serializeYesplanAPIDate(startDateObject)
      },
      (res, error) => {
        if (error) {
          this.setState({
            errorCode: error.code
          });
        } else if (res.statusCode !== 440) {
          return res.body;
        }
      }
    );
  }

  fetchTimesheetAndOnlyUpdateHistory(dateObject) {
    this.getTimesheet(dateObject).then((fetchedTimesheet) => {
      if (fetchedTimesheet.history && fetchedTimesheet.history.length) {
        this.setState((prevState) => {
          const timesheetFromStateWithBookingInfo = prevState.timesheet;
          timesheetFromStateWithBookingInfo.setTimesheetHistory(fetchedTimesheet.history);
          return {
            timesheet: timesheetFromStateWithBookingInfo
          };
        });
      }
    });
  }

  /* Counters are currently not used, so for now it only gets the timesheet */
  fetchTimesheetAndCounters(dateObject) {
    const { getCacheResultValue } = this.props;
    const dateString = serializeYesplanAPIDate(dateObject);
    const cachedTimesheets = getCacheResultValue(cacheResultForTimesheetsKey);
    if (cachedTimesheets && cachedTimesheets[dateString]) {
      const timesheet = cachedTimesheets[dateString];
      this.fetchBookingAndEventDetails(timesheet);
    } else {
      this.getTimesheet(dateObject).then((timesheet) => {
        this.fetchBookingAndEventDetails(formatTimesheet(timesheet, dateString));
      });
    }
  }

  fetchBookingAndEventDetails(timesheet) {
    const resourceBookingUrls = timesheet.schedules
      .filter((schedule) => schedule.scheduletype === 'resourcebooking')
      .map((schedule) => schedule.resourcebooking.url);
    if (resourceBookingUrls.length > 0) {
      new RequestHandler().get(
        '/resourceBookingDetails',
        {
          yesplanurl: this.props.user.host,
          bookingurls: resourceBookingUrls
        },
        (res, error) => {
          if (error)
            this.setState({
              errorCode: error.code
            });
          else if (res.statusCode !== 440) {
            const { bookings, events } = res.body;
            timesheet.extendAndSortEntriesBasedOnBookingDetails(bookings, events);
            this.setState({
              timesheet,
              allContentFetched: true
            });
          }
        }
      );
    } else {
      timesheet.extendAndSortEntriesBasedOnBookingDetails([], []);
      this.setState({
        timesheet,
        allContentFetched: true
      });
    }
  }

  autosaveTimesheet(timesheet) {
    const { user } = this.props;
    if (timesheet.hasChanged() && timesheet.canEdit()) {
      new RequestHandler().put(
        '/timesheet',
        {
          yesplanurl: user.host,
          resourceid: user.resource.id,
          on: serializeYesplanAPIDate(timesheet.getDate())
        },
        timesheet.serialize(false),
        (res, error) => {
          /* TODO for now we ignore errors,
             if the user leaves this page, we try to perform a final autosave as a fallback mechanism */
        }
      );
    }
  }

  submitTimesheet(timesheet, callbackFn = null) {
    const { user, humanResource } = this.props;
    const dateObject = timesheet.getDate();
    new RequestHandler().put(
      '/timesheet',
      {
        yesplanurl: user.host,
        resourceid: user.resource.id,
        on: serializeYesplanAPIDate(dateObject)
      },
      timesheet.serialize(true, YPTimesheetObject.SUBMITTED_STATUS),
      (res, error) => {
        if (res.statusCode === 200) {
          if (humanResource?.timesheetrequiresapproval) {
            timesheet.setStatusToSubmitted();
          } else {
            timesheet.setStatusToFinalized();
          }
          timesheet.setTimesheetTimesForUnEditedEntries();
          this.fetchTimesheetAndOnlyUpdateHistory(dateObject);
          capture(PH_TIMESHEET_SUBMITTED, {
            date: serializeYesplanAPIDate(dateObject),
            requiresApproval: !!humanResource?.timesheetrequiresapproval,
          });
          if (callbackFn) callbackFn();
        }
      }
    );
  }

  renderErrorMessage() {
    const { widgetPageHeaderRef, navigationHistory } = this.props;
    const { errorCode } = this.state;

    return (
      <ContainerWrapper
        $paddingTop={
          widgetPageHeaderRef.current ? widgetPageHeaderRef.current.getHeaderHeight() : 0
        }
        data-test-type="errorMessage"
      >
        <TimesheetError errorCode={errorCode} navigationHistory={navigationHistory} />
      </ContainerWrapper>
    );
  }

  renderNoPermission(Component) {
    return (
      <>
        <div style={{ paddingTop: '48px' }} />
        <Component title={i18n('Sorry! No timesheets for you… yet')} />
      </>
    );
  }

  render() {
    const { humanResource } = this.props;
    const { errorCode, allContentFetched, timesheet } = this.state;
    if (errorCode) return this.renderErrorMessage();
    if (humanResource === null) return this.renderNoPermission(NoPermissionOnResourceError);
    if (!humanResource.usetimesheets) return this.renderNoPermission(NotInTeamUsingTimesheetsError);
    if (!allContentFetched) return <LoadingState $show={!allContentFetched} />;

    return (
      <TimesheetDetail
        counters={[]}
        timesheetObject={timesheet}
        dateObject={this.dateObject}
        autosaveTimesheet={this.autosaveTimesheet}
        submitTimesheet={this.submitTimesheet}
        {...this.props}
      />
    );
  }
}

TimesheetContainer.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    url: PropTypes.string,
    resource: PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  }).isRequired,
  humanResource: PropTypes.oneOfType([
    () => null,
    PropTypes.shape({
      timesheetrequiresapproval: PropTypes.bool
    })
  ]).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  previousURL: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired,
  widgetAnimationWrapperRef: PropTypes.shape({
    current: PropTypes.oneOfType([() => null, PropTypes.shape({})])
  }).isRequired,
  router: PropTypes.shape({
    params: PropTypes.shape({
      dateString: PropTypes.string
    }).isRequired
  }).isRequired,
  widgetPageHeaderRef: PropTypes.shape({}),
  getCacheResultValue: PropTypes.func.isRequired,
  resetCacheResultValue: PropTypes.func.isRequired,
  getDateFromUrlOrFromCurrentDate: PropTypes.func.isRequired,
  replaceUrlWithDateUrl: PropTypes.func.isRequired,
  linkUrlFor: PropTypes.func.isRequired
};

TimesheetContainer.defaultProps = {
  widgetPageHeaderRef: null
};

TimesheetContainer.contextType = FormatSettingsContext;

function ForwardRef(props, ref) {
  return (
    <FormatSettingsContext.Consumer>
      {(contextProps) => (
        <CacheResultsContext.Consumer>
          {(cacheResultsContextProps) => (
            <DateNavigationUrlWrapper
              {...props}
              {...cacheResultsContextProps}
              headerRef={ref}
            >
              {(propsFromWrapper) => (
                <TimesheetContainer {...contextProps} {...propsFromWrapper} />
              )}
            </DateNavigationUrlWrapper>
          )}
        </CacheResultsContext.Consumer>
      )}
    </FormatSettingsContext.Consumer>
  );
}

export default withRouter(React.forwardRef(ForwardRef));
